import { useEffect, useState } from 'react';
import { MyBookings } from './MyBookings';
import { MembershipCard, MembershipPage, membershipQuery, useMyMemberships, type AppMembership } from './Membership';
import {
  NotificationsPage,
  NotificationsRow,
  RecommendationPage,
  RecommendationRow,
  useMyRecommendation,
} from './Recommendation';
import { startPush } from './push';
import './client-app.css';
import { BrandSplash } from '../splash/BrandSplash';

/**
 * Client app on the phone (PWA, owner 22.10.2026): hub.360face.pt/app, added to the home screen —
 * no App Store. Start screen with the three things a client does; each opens the existing pages.
 * English and Portuguese together (studios in Portugal, many expat clients).
 * Tabs (App Store, owner 22.10.2026): Book · My bookings · Contacts — /app, /app/bookings, /app/contacts.
 */
const CLIENT_APP_FLAG = 'hub-client-app';
const WHATSAPP = 'https://wa.me/351915311522';
const INSTAGRAM = 'https://instagram.com/360face_';

/** Marks this phone as a client app, so the studio-kiosk mode (keyboard, idle reset) never applies. */
export function markClientApp(): void {
  try {
    window.localStorage.setItem(CLIENT_APP_FLAG, '1');
  } catch {
    // storage blocked: nothing to remember
  }
}

export function isClientApp(): boolean {
  try {
    return window.localStorage.getItem(CLIENT_APP_FLAG) === '1';
  } catch {
    return false;
  }
}

type Platform = 'ios' | 'android' | 'other';

function platform(ua: string): Platform {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/**
 * Opened inside the App Store app (Capacitor shell, mobile/): no "install" hint there. The shell
 * appends «360FACE-App» to the user agent (capacitor.config.json) and injects window.Capacitor.
 */
export function isNativeApp(): boolean {
  const w = window as Window & { Capacitor?: { isNativePlatform?: () => boolean } };
  return /360FACE-App/.test(navigator.userAgent) || w.Capacitor?.isNativePlatform?.() === true;
}

function installed(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return isNativeApp() || nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

export type Tab = 'home' | 'bookings' | 'contacts' | 'recommendation';

const TAB_PATH: Record<Tab, string> = {
  home: '/app',
  bookings: '/app/bookings',
  contacts: '/app/contacts',
  recommendation: '/app/recommendation',
};

export function tabFromPath(path: string): Tab {
  if (path.startsWith('/app/bookings')) return 'bookings';
  if (path.startsWith('/app/recommendation')) return 'recommendation';
  if (path.startsWith('/app/contacts')) return 'contacts';
  return 'home';
}

/**
 * Studios for the picker (owner 26.10.2026, variant E): a country row on top, then the studios of
 * that country — each with its own 3D picture. Saldanha (Lisboa) is planned and stays hidden.
 */
export interface Studio {
  id: string;
  country: 'PT';
  name: string;
  area: string;
  contactName: string;
  address: string;
  team: string;
  image: string;
  hidden?: boolean;
}
export const STUDIOS: Studio[] = [
  {
    id: 'parede',
    country: 'PT',
    name: 'Cascais',
    area: 'Parede · R. Almeida Garrett 112B',
    contactName: 'Parede',
    address: 'Rua Almeida Garrett 112B, 2785-344 São Domingos de Rana',
    team: 'Olena · Alyona',
    image: '/v3/studios/cascais.png',
  },
  {
    id: 'lisboa',
    country: 'PT',
    name: 'Lisboa · Campo de Ourique',
    area: 'Rua Azedo Gneco 48B',
    contactName: 'Lisboa',
    address: 'Rua Azedo Gneco 48B, 1350-036 Lisboa',
    team: 'Olena · Alyona · Ekaterina',
    image: '/v3/studios/lisboa-campo-de-ourique.png',
  },
  {
    id: 'saldanha',
    country: 'PT',
    name: 'Lisboa · Saldanha',
    area: 'Av. da República',
    contactName: 'Saldanha',
    address: 'Avenida da República, Lisboa',
    team: '',
    image: '/v3/studios/lisboa-saldanha.png',
    hidden: true,
  },
];

/**
 * Owner 26.10.2026: the first start asks PT or EN under the splash and remembers it; later the
 * chip at the top opens a small menu with five languages. Never asked again on start.
 */
export type AppLang = 'pt' | 'en' | 'fr' | 'es' | 'ru';
export const APP_LANGS: Array<[AppLang, string]> = [
  ['pt', 'Português'],
  ['en', 'English'],
  ['fr', 'Français'],
  ['es', 'Español'],
  ['ru', 'Русский'],
];
const isAppLang = (v: unknown): v is AppLang => APP_LANGS.some(([l]) => l === v);
const LANG_KEY = 'hub.app.lang';
const STUDIO_KEY = 'hub.app.studio';

function readKey(k: string): string | null {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
}
function writeKey(k: string, v: string) {
  try {
    window.localStorage.setItem(k, v);
  } catch {
    // storage blocked: asked again next time
  }
}
export function appLang(): AppLang | undefined {
  const v = readKey(LANG_KEY);
  return isAppLang(v) ? v : undefined;
}

type Action = 'book' | 'checkin' | 'gift';

const T = {
  pt: {
    hello: 'Olá! O que deseja hoje?',
    book: ['Marcar tratamento', 'Rosto, corpo, pele'],
    mine: ['As minhas marcações', 'Ver, confirmar, alterar'],
    checkin: ['Check-in rápido', 'Tenho marcação hoje'],
    gifts: ['Presentes', 'Cartão-oferta · Gift box'],
    where: 'Onde quer ser atendida?',
    last: 'Última visita',
    back: 'Voltar',
    contacts: 'Contactos',
  },
  en: {
    hello: 'Hello! What would you like today?',
    book: ['Book a treatment', 'Face, body, skin'],
    mine: ['My bookings', 'See, confirm, change'],
    checkin: ['Quick check-in', 'I have a booking today'],
    gifts: ['Gifts', 'Gift card · Gift box'],
    where: 'Where would you like your treatment?',
    last: 'Last visit',
    back: 'Back',
    contacts: 'Contacts',
  },
  fr: {
    hello: 'Bonjour ! Que souhaitez-vous aujourd’hui ?',
    book: ['Réserver un soin', 'Visage, corps, peau'],
    mine: ['Mes rendez-vous', 'Voir, confirmer, modifier'],
    checkin: ['Check-in rapide', 'J’ai un rendez-vous aujourd’hui'],
    gifts: ['Cadeaux', 'Carte cadeau · Gift box'],
    where: 'Où souhaitez-vous votre soin ?',
    last: 'Dernière visite',
    back: 'Retour',
    contacts: 'Contacts',
  },
  es: {
    hello: '¡Hola! ¿Qué desea hoy?',
    book: ['Reservar un tratamiento', 'Rostro, cuerpo, piel'],
    mine: ['Mis citas', 'Ver, confirmar, cambiar'],
    checkin: ['Check-in rápido', 'Tengo cita hoy'],
    gifts: ['Regalos', 'Tarjeta regalo · Gift box'],
    where: '¿Dónde quiere su tratamiento?',
    last: 'Última visita',
    back: 'Atrás',
    contacts: 'Contacto',
  },
  ru: {
    hello: 'Здравствуйте! Что вы хотите сегодня?',
    book: ['Записаться на процедуру', 'Лицо, тело, кожа'],
    mine: ['Мои записи', 'Посмотреть, подтвердить, перенести'],
    checkin: ['Быстрый check-in', 'У меня запись сегодня'],
    gifts: ['Подарки', 'Подарочная карта · Gift box'],
    where: 'Где вам удобнее?',
    last: 'Последний визит',
    back: 'Назад',
    contacts: 'Контакты',
  },
} as const;

const ART: Record<Action | 'mine', string> = {
  book: '/v3/s-lifting.jpg',
  mine: '/v3/t-visits.png',
  checkin: '/v3/t-home.png',
  gift: '/v3/gift.png',
};

/** Where each action continues once the studio is chosen. */
export function actionUrl(action: Action, studio: string, lang: AppLang): string {
  const q = `location=${encodeURIComponent(studio)}&app=${action}&lang=${lang}`;
  return action === 'checkin' ? `/pre?${q}` : `/v3?${q}`;
}

function mapUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`360°FACE ${address}`)}`;
}

interface InstallPrompt extends Event {
  prompt(): Promise<void>;
}

const SPLASH_KEY = 'hub.splash.seen';
function splashSeen(): boolean {
  try {
    return sessionStorage.getItem(SPLASH_KEY) === '1';
  } catch {
    return true;
  }
}
function markSplashSeen() {
  try {
    sessionStorage.setItem(SPLASH_KEY, '1');
  } catch {
    // private mode — the splash plays again next time
  }
}

export function ClientApp({
  fetchImpl,
  splash = import.meta.env.MODE !== 'test',
}: { fetchImpl?: typeof fetch; splash?: boolean } = {}) {
  const [tab, setTab] = useState<Tab>(() => tabFromPath(window.location.pathname));
  // Owner 26.10.2026: PT or EN, chosen once under the splash on the first start.
  const [lang, setLang] = useState<AppLang | undefined>(() => appLang() ?? (splash ? undefined : 'en'));
  // The brand splash once per session (owner 23.10.2026); the first time with the languages.
  const [showSplash, setShowSplash] = useState(() => splash && (!appLang() || !splashSeen()));

  useEffect(() => {
    if (lang) startPush(lang, fetchImpl ?? defaultFetch);
  }, [lang, fetchImpl]);
  useEffect(() => {
    markClientApp();
    const onPop = () => setTab(tabFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = (t: Tab) => {
    if (t === tab) return;
    try {
      window.history.pushState(null, '', TAB_PATH[t]);
    } catch {
      // history blocked (embedded preview): the page still switches
    }
    if (!/jsdom/i.test(navigator.userAgent)) window.scrollTo(0, 0);
    setTab(t);
  };
  const choose = (l: AppLang) => {
    writeKey(LANG_KEY, l);
    setLang(l);
    markSplashSeen();
    setShowSplash(false);
  };

  if (showSplash || !lang) {
    const first = !lang;
    return (
      <BrandSplash
        className={first ? 'ca-splash-langs' : undefined}
        onDone={() => {
          if (first) return;
          markSplashSeen();
          setShowSplash(false);
        }}
      >
        {first && (
          <div className="ca-langs">
            <button type="button" className="ca-lang" onClick={() => choose('pt')}>
              Português
            </button>
            <button type="button" className="ca-lang" onClick={() => choose('en')}>
              English
            </button>
          </div>
        )}
      </BrandSplash>
    );
  }

  const t = T[lang];
  const back = (
    <button type="button" className="ca-back" onClick={() => go('home')}>
      ‹ {t.back}
    </button>
  );
  return (
    <div className="ca-shell ca-shell-v3">
      {tab === 'home' && (
        <Home
          lang={lang}
          onLang={choose}
          onMine={() => go('bookings')}
          onContacts={() => go('contacts')}
          onReco={() => go('recommendation')}
          fetchImpl={fetchImpl ?? defaultFetch}
        />
      )}
      {tab === 'recommendation' && (
        <RecoScreen
          lang={lang}
          fetchImpl={fetchImpl ?? defaultFetch}
          onHome={() => go('home')}
          onSignIn={() => go('bookings')}
        />
      )}
      {tab === 'bookings' && (
        <>
          {back}
          <MyBookings lang={lang} {...(fetchImpl ? { fetchImpl } : {})} />
        </>
      )}
      {tab === 'contacts' && (
        <>
          {back}
          <Contacts />
        </>
      )}
    </div>
  );
}

/** The recommendation page (also opened by the push «…left you recommendations»). */
function RecoScreen({
  lang,
  fetchImpl,
  onHome,
  onSignIn,
}: {
  lang: AppLang;
  fetchImpl: typeof fetch;
  onHome: () => void;
  onSignIn: () => void;
}) {
  const s = useMyRecommendation(fetchImpl, lang);
  useEffect(() => {
    if (s.kind === 'signed_out') onSignIn();
    else if (s.kind === 'none' || s.kind === 'error') onHome();
  }, [s.kind, onHome, onSignIn]);
  if (s.kind !== 'ready') return <main className="ca3" aria-busy="true" />;
  return (
    <RecommendationPage
      r={s.r}
      lang={lang}
      onBack={onHome}
      onBook={() => {
        writeKey(STUDIO_KEY, s.r.locationId);
        window.location.assign(actionUrl('book', s.r.locationId, lang));
      }}
    />
  );
}

function Contacts() {
  return (
    <section className="ca-page" aria-label="Contacts">
      <h1 className="ca-h1">
        Contacts <small>Contactos</small>
      </h1>
      <ul className="ca-list">
        {STUDIOS.filter((s) => !s.hidden).map((s) => (
          <li key={s.id} className="ca-card">
            <b className="ca-what">360°FACE {s.contactName}</b>
            <p className="ca-muted">{s.address}</p>
            <a className="ca-chip" href={mapUrl(s.address)} rel="noopener noreferrer">
              Directions · Como chegar
            </a>
          </li>
        ))}
      </ul>
      <nav className="ca-actions">
        <a className="ca-btn" href={WHATSAPP} rel="noopener noreferrer">
          <b>WhatsApp</b>
          <span>+351 915 311 522 · change or cancel a booking</span>
        </a>
        <a className="ca-btn" href="tel:+351915311522">
          <b>Call us · Ligar</b>
          <span>+351 915 311 522</span>
        </a>
        <a className="ca-btn" href="mailto:info@360face.pt">
          <b>E-mail</b>
          <span>info@360face.pt</span>
        </a>
        <a className="ca-btn" href={INSTAGRAM} rel="noopener noreferrer">
          <b>Instagram</b>
          <span>@360face_</span>
        </a>
      </nav>
      <p className="ca-hint ca-pad">
        <a href="/privacy">Privacy policy · Política de privacidade</a>
        <br />
        SENTIDINVULGAR, UNIPESSOAL, LDA · NIPC 518136574
      </p>
    </section>
  );
}

const defaultFetch: typeof fetch = (...a) => fetch(...a);

function Home({
  lang,
  onLang,
  onMine,
  onContacts,
  onReco,
  fetchImpl,
}: {
  lang: AppLang;
  onLang: (l: AppLang) => void;
  onMine: () => void;
  onContacts: () => void;
  onReco: () => void;
  fetchImpl: typeof fetch;
}) {
  const memberships = useMyMemberships(fetchImpl);
  const reco = useMyRecommendation(fetchImpl, lang);
  const [notifOpen, setNotifOpen] = useState(false);
  const native = isNativeApp();
  // the membership page is open / booking with this membership (owner 26.10.2026)
  const [memOpen, setMemOpen] = useState<AppMembership>();
  const [withMem, setWithMem] = useState<AppMembership>();
  const [langOpen, setLangOpen] = useState(false);
  const [prompt, setPrompt] = useState<InstallPrompt>();
  const [standalone] = useState(installed);
  // Owner 26.10.2026: «Book», «Quick check-in» and «Gifts» ask the studio first (variant E).
  const [pick, setPick] = useState<Action | undefined>(() => {
    const p = new URLSearchParams(window.location.search).get('pick');
    return p === 'book' || p === 'checkin' || p === 'gift' ? p : undefined;
  });
  const os = platform(navigator.userAgent);
  const t = T[lang];

  useEffect(() => {
    const on = (e: Event) => {
      e.preventDefault();
      setPrompt(e as InstallPrompt);
    };
    window.addEventListener('beforeinstallprompt', on);
    return () => window.removeEventListener('beforeinstallprompt', on);
  }, []);

  const top = (
    <header className="ca3-top">
      <a className="ca3-logo" href="/app" aria-label="360°FACE">
        360<i>°</i>FACE
      </a>
      <div className="ca3-langwrap">
        <button
          type="button"
          className="ca3-chip"
          aria-haspopup="listbox"
          aria-expanded={langOpen}
          onClick={() => setLangOpen((x) => !x)}
        >
          {APP_LANGS.find(([l]) => l === lang)?.[1]} ▾
        </button>
        {langOpen && (
          <ul className="ca3-langmenu" role="listbox" aria-label="Language">
            {APP_LANGS.map(([l, name]) => (
              <li key={l}>
                <button
                  type="button"
                  role="option"
                  aria-selected={l === lang}
                  onClick={() => {
                    setLangOpen(false);
                    onLang(l);
                  }}
                >
                  {name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </header>
  );

  if (pick) {
    const last = readKey(STUDIO_KEY);
    return (
      <main className="ca3">
        {top}
        <h1 className="ca3-h1">{t.where}</h1>
        <div className="ca3-countries" role="tablist" aria-label="Country">
          <span className="ca3-country on" role="tab" aria-selected="true">
            🇵🇹 Portugal
          </span>
        </div>
        <div className="ca3-list">
          {STUDIOS.filter((st) => st.country === 'PT' && !st.hidden).map((st) => (
            <a
              key={st.id}
              className={st.id === last ? 'ca3-studio last' : 'ca3-studio'}
              href={actionUrl(pick, st.id, lang) + (pick === 'book' && withMem ? membershipQuery(withMem, st.id) : '')}
              onClick={() => writeKey(STUDIO_KEY, st.id)}
            >
              {st.id === last && <span className="ca3-badge">{t.last}</span>}
              <img src={st.image} alt="" />
              <span className="ca3-studio-text">
                <b>{st.name}</b>
                <small>{st.area}</small>
                <em>{st.team}</em>
              </span>
              <span className="ca3-go" aria-hidden>
                ›
              </span>
            </a>
          ))}
        </div>
        <div className="ca3-rail">
          <button
            type="button"
            className="ca3-btn"
            onClick={() => {
              setPick(undefined);
              setWithMem(undefined);
            }}
          >
            {t.back}
          </button>
        </div>
      </main>
    );
  }

  if (memOpen) {
    return (
      <MembershipPage
        m={memOpen}
        lang={lang}
        onBack={() => setMemOpen(undefined)}
        onBook={() => {
          setWithMem(memOpen);
          setMemOpen(undefined);
          setPick('book');
        }}
      />
    );
  }

  if (notifOpen) {
    return (
      <NotificationsPage
        lang={lang}
        studios={STUDIOS.filter((st) => !st.hidden).map((st) => ({ id: st.id, name: st.name }))}
        signedIn={reco.kind !== 'signed_out'}
        fetchImpl={fetchImpl}
        onBack={() => setNotifOpen(false)}
      />
    );
  }

  const card = (key: Action | 'mine', text: readonly [string, string], onClick: () => void, red = false) => (
    <button type="button" className={red ? 'ca3-card red' : 'ca3-card'} onClick={onClick}>
      <img src={ART[key]} alt="" />
      <span>
        <b>{text[0]}</b>
        <small>{text[1]}</small>
      </span>
    </button>
  );
  return (
    <main className="ca3">
      {top}
      <h1 className="ca3-h1">{t.hello}</h1>
      <nav className="ca3-list">
        {memberships.map((m, i) => (
          <MembershipCard key={m.id ?? i} m={m} lang={lang} onOpen={() => setMemOpen(m)} />
        ))}
        <RecommendationRow s={reco} lang={lang} onOpen={onReco} onSignIn={onMine} />
        {card('book', t.book, () => setPick('book'), true)}
        {card('mine', t.mine, onMine)}
        {card('checkin', t.checkin, () => setPick('checkin'))}
        {card('gift', t.gifts, () => setPick('gift'))}
        {native && <NotificationsRow lang={lang} onOpen={() => setNotifOpen(true)} />}
      </nav>
      {!standalone && (
        <section className="ca-install" aria-label="Install">
          {prompt ? (
            <button className="ca-btn ca-btn-dark" onClick={() => void prompt.prompt()}>
              <b>Install the app</b>
              <span>Instalar a aplicação</span>
            </button>
          ) : os === 'ios' ? (
            <p>
              <b>Add to your Home Screen:</b> tap <span aria-label="Share">Share ⬆︎</span> → «Add to Home Screen».
              <br />
              Adicionar ao ecrã principal: Partilhar ⬆︎ → «Adicionar ao ecrã principal».
            </p>
          ) : (
            <p>
              <b>Install:</b> browser menu ⋮ → «Install app» / «Add to Home screen».
              <br />
              Instalar: menu ⋮ → «Instalar aplicação».
            </p>
          )}
        </section>
      )}
      <footer className="ca3-foot">
        <button type="button" onClick={onContacts}>
          {t.contacts}
        </button>
        <span>·</span>
        <a href={WHATSAPP} rel="noopener noreferrer">
          WhatsApp
        </a>
        <span>·</span>
        <a href={INSTAGRAM} rel="noopener noreferrer">
          Instagram
        </a>
        <span>·</span>
        <a href="/privacy">Privacy</a>
      </footer>
    </main>
  );
}
